/* @flow */
/* eslint-disable react/no-children-prop */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Link, Route } from 'react-router-dom';
import { hasURLProtocol } from '@e180/util/lib/url';

import styles from './MainNavLink.style';

import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';
import { capitalize } from 'src/shared/core/util/stringUtil';

import type { ReactElement } from 'src/shared/core/type/reactTypes';

type Props = {
  title: string,
  to: string,
  exact?: boolean,
  icon?: React.Node,
  children?: React.Node,
  preloadComp?: ReactElement,
  target: ?string,
};

const useStyles = createUseStyles(styles);

const MainNavLink = ({
  title,
  to,
  exact,
  icon,
  children,
  preloadComp,
  target,
}: Props): React.Node => {
  const isExternalLink = hasURLProtocol(to);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [accentColor] = useEventAccentColor();

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onMouseOver = () => {
    if (preloadComp) {
      // $FlowIssue
      preloadComp.preload();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ accentColor });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Route
      path={to}
      exact={exact}
      children={({ match }) => {
        const rootClasses = classNames(classes.root, {
          [`${classes['root-isSelected']}`]: match,
        });

        let props = {
          className: rootClasses,
        };

        if (isExternalLink) {
          props = {
            ...props,
            href: to,
            target,
          };
        } else {
          props = {
            ...props,
            to,
            onMouseOver,
          };
        }

        return React.createElement(
          isExternalLink ? 'a' : Link,
          // $FlowIssue: `to` is passed if element is `Link`
          props,
          <React.Fragment>
            <span className={classes.icon}>{icon}</span>
            <span className={classes.label}>{capitalize(title)}</span>
            {children}
          </React.Fragment>,
        );
      }}
    />
  );
};

export default compose(React.memo)(MainNavLink);
